import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { assessmentsData } from '../data/assessments';

const { FiArrowLeft, FiClock, FiUsers, FiStar, FiCheckCircle, FiShoppingCart, FiPlay, FiFileText } = FiIcons;

const AssessmentDetail = () => {
  const { id } = useParams();
  const [selectedTab, setSelectedTab] = useState('overview');
  
  const assessment = assessmentsData.find(a => a.id === parseInt(id));

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Assessment not found</h2>
          <Link to="/assessments" className="text-blue-600 hover:underline">
            Back to assessments
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'details', name: 'Details' },
    { id: 'sample', name: 'Sample Questions' },
    { id: 'reviews', name: 'Reviews' }
  ];

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'basic': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/assessments"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
            Back to Assessments
          </Link>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getComplexityColor(assessment.complexity)}`}>
                    {assessment.complexity.charAt(0).toUpperCase() + assessment.complexity.slice(1)}
                  </span>
                  <span className="text-gray-500">
                    {assessment.category.charAt(0).toUpperCase() + assessment.category.slice(1)}
                  </span>
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {assessment.title}
                </h1>
                
                <p className="text-lg text-gray-600 mb-6">
                  {assessment.description}
                </p>
                
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5" />
                    <span>{assessment.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiUsers} className="w-5 h-5" />
                    <span>{assessment.questions} questions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiStar} className="w-5 h-5" />
                    <span>{assessment.rating} (127 reviews)</span>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Purchase Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-6">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    ${assessment.price}
                  </div>
                  <p className="text-gray-600">One-time assessment</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center">
                    <SafeIcon icon={FiShoppingCart} className="w-5 h-5 mr-2" />
                    Purchase Assessment
                  </button>
                  <button className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center">
                    <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" />
                    Preview Questions
                  </button>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      Interactive assessment experience
                    </li>
                    <li className="flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      Comprehensive analysis report
                    </li>
                    <li className="flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      Actionable recommendations
                    </li>
                    <li className="flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      Performance benchmarking
                    </li>
                    {assessment.hasExpertReview && (
                      <li className="flex items-center">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        Expert consultant review
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-8">
              <nav className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      selectedTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <motion.div
              key={selectedTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-lg p-6"
            >
              {selectedTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Assessment Overview</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {assessment.fullDescription || assessment.description}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Key Benefits</h3>
                    <ul className="space-y-2">
                      {assessment.benefits?.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{benefit}</span>
                        </li>
                      )) || [
                        'Identify key strengths and weaknesses',
                        'Receive actionable improvement recommendations',
                        'Benchmark against industry standards',
                        'Get clear next steps for implementation'
                      ].map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {selectedTab === 'details' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Assessment Details</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Duration</h4>
                        <p className="text-gray-600">{assessment.duration}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Number of Questions</h4>
                        <p className="text-gray-600">{assessment.questions} questions</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Complexity Level</h4>
                        <p className="text-gray-600 capitalize">{assessment.complexity}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Expert Review</h4>
                        <p className="text-gray-600">
                          {assessment.hasExpertReview ? 'Included' : 'AI-powered only'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Report Format</h4>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="w-5 h-5 text-blue-600 mr-2" />
                        <span className="text-gray-600">PDF Report</span>
                      </div>
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="w-5 h-5 text-blue-600 mr-2" />
                        <span className="text-gray-600">Interactive Dashboard</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'sample' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Sample Questions</h3>
                  <div className="space-y-4">
                    {assessment.sampleQuestions?.map((question, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <p className="font-medium text-gray-900 mb-2">
                          Question {index + 1}
                        </p>
                        <p className="text-gray-600">{question}</p>
                      </div>
                    )) || [
                      'How would you rate your current business performance compared to your goals?',
                      'What are the biggest challenges your organization is currently facing?',
                      'How effectively does your team communicate and collaborate?',
                      'What metrics do you use to measure success in your business?'
                    ].map((question, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <p className="font-medium text-gray-900 mb-2">
                          Question {index + 1}
                        </p>
                        <p className="text-gray-600">{question}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTab === 'reviews' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Customer Reviews</h3>
                  <div className="space-y-6">
                    {[
                      {
                        name: 'Sarah Johnson',
                        company: 'Tech Startup CEO',
                        rating: 5,
                        review: 'This assessment provided incredible insights into our operational inefficiencies. The recommendations were actionable and helped us improve our processes significantly.'
                      },
                      {
                        name: 'Michael Chen',
                        company: 'Manufacturing Director',
                        rating: 4,
                        review: 'Comprehensive analysis that highlighted areas we hadn\'t considered. The expert review session was particularly valuable for our strategic planning.'
                      },
                      {
                        name: 'Emily Rodriguez',
                        company: 'Marketing Manager',
                        rating: 5,
                        review: 'Easy to complete and the results were presented in a clear, understandable format. Great value for the price.'
                      }
                    ].map((review, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{review.name}</h4>
                            <p className="text-sm text-gray-500">{review.company}</p>
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <SafeIcon
                                key={i}
                                icon={FiStar}
                                className={`w-4 h-4 ${
                                  i < review.rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600">{review.review}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetail;